import { ConnectError } from '@bufbuild/connect';
import {
  DocumentViewer,
  LinkButton,
  PrimaryButton,
  ReportButton,
  deserify,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  useCommonServiceClientContext,
  useFeatureEnabled,
} from '@geneo2-web/shared-ui';
import { Box, Typography } from '@mui/material';
import { FileEnum } from '@protos/content_management/content.db_pb';
import { Feature } from '@protos/school_management/school.db_pb';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalContext } from '../../../app/Context/GlobalContextProvider';
import { useAppSelector } from '../../../reduxStore/reduxHooks';
import { setToastInfo } from '../../Home/reducer/homeDashboard.slice';
import { interactionEvent } from '../../Auth/auth.events';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../app/Context/DownloadContextProviderV2';

interface IViewReport {
  connectedClassSessionId: bigint;
  reportButtonHandler?: () => void;
  subject?: string;
  className?: string;
  section?: string;
}

export default function ViewConnectedClassReportButton(props: IViewReport) {
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { SchoolAssessmentAPIServiceV1ClientWithStatusCodeHandler } =
    useCommonServiceClientContext();
  const user = deserify(useAppSelector((state) => state.auth.user_info));
  const featureInfo = user?.schoolDetails[0]?.featuresPlanInfo;
  const isViewReportsEnabled = useFeatureEnabled(
    featureInfo,
    Feature.VIEW_REPORTS
  );
  const { setShowAppLoader } = useGlobalContext();
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined);
  const dispatch = useDispatch();

  // ------------------------------------------------------------

  const fetchConnectedClassAssessmentReport = async () => {
    try {
      setShowAppLoader(true);
      const res =
        await SchoolAssessmentAPIServiceV1ClientWithStatusCodeHandler.fetchConnectedClassroomAssessmentReport(
          {
            connectedClassSessionId: props.connectedClassSessionId,
            teacherId: user?.teacherProfileId,
          }
        );
      if (res.data?.gcpReportUrl) {
        setPdfUrl(getMediaBasePath(res.data.gcpReportUrl));
      } else {
        dispatch(
          setToastInfo({
            label: res.message,
            variant: 'success',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    } catch (err) {
      if (err instanceof ConnectError) {
        console.log('Error:', err.rawMessage);
        dispatch(
          setToastInfo({
            label: err.rawMessage,
            variant: 'error',
            open: true,
          })
        );
      } else {
        dispatch(
          setToastInfo({
            label: 'Error while generating report',
            variant: 'error',
            open: true,
          })
        );
      }
      setShowAppLoader(false);
    }
  };

  // ------------------------------------------------------------

  if (!isViewReportsEnabled) {
    return null;
  }
  return (
    <>
      <ReportButton
        buttonText="View Report"
        buttonList={[
          {
            name: 'View Report',
            onClick: async () => {
              props.reportButtonHandler
                ? props.reportButtonHandler()
                : fetchConnectedClassAssessmentReport();
              await interactionEvent({
                context: 'Your_Assessment',
                name: 'TEACHER_CONNECTED_CLASSROOM_REPORT',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            },
          },
        ]}
      />
      {pdfUrl && (
        <DocumentViewer
          downloadUrl={pdfUrl}
          previewUrl={pdfUrl}
          gcpUrl={pdfUrl}
          type={FileEnum.FILE_TYPE_PDF}
          fileName={
            props.subject || props.className || props.section
              ? `${props.subject || ''}_${props.className || ''}_${
                  props.section || ''
                }_Geneo_Assessment_Report_Teacher_${
                  props.connectedClassSessionId
                }.pdf`
              : `Geneo_Assessment_Report_Teacher_${props.connectedClassSessionId}.pdf`
          }
          open={!!pdfUrl}
          closeFile={() => {
            setPdfUrl(undefined);
          }}
        />
      )}
    </>
  );
}
